import { Slide, SlideGeneratorState } from '../types';

interface SlideProject {
  id: string;
  name: string;
  slides: Slide[];
  lastModified: number;
}

interface UserPreferences {
  id: string;
  collapsedSections: {[key: string]: boolean};
  propertyPanelPosition: { x: number; y: number };
  hideKeyboardHints: boolean;
  lastModified: number;
}

interface AppStateBackup {
  id: string;
  state: Partial<SlideGeneratorState>;
  slides: Slide[];
  activeSlideId: string | null;
  timestamp: number;
}

class IndexedDBService {
  private dbName = 'slidemaker-db';
  private version = 2; // Increment version for new stores
  private db: IDBDatabase | null = null;

  async initDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => {
        console.error('IndexedDB failed to open');
        reject(new Error('Failed to open IndexedDB'));
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        
        // Create projects store
        if (!this.db.objectStoreNames.contains('projects')) {
          const projectStore = this.db.createObjectStore('projects', { keyPath: 'id' });
          projectStore.createIndex('lastModified', 'lastModified', { unique: false });
        }

        // Create drafts store for auto-save
        if (!this.db.objectStoreNames.contains('drafts')) {
          const draftStore = this.db.createObjectStore('drafts', { keyPath: 'id' });
          draftStore.createIndex('lastModified', 'lastModified', { unique: false });
        }

        // Create user preferences store
        if (!this.db.objectStoreNames.contains('preferences')) {
          const prefsStore = this.db.createObjectStore('preferences', { keyPath: 'id' });
          prefsStore.createIndex('lastModified', 'lastModified', { unique: false });
        }

        // Create app state backups store
        if (!this.db.objectStoreNames.contains('backups')) {
          const backupStore = this.db.createObjectStore('backups', { keyPath: 'id' });
          backupStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  async saveProject(project: SlideProject): Promise<void> {
    if (!this.db) {await this.initDB();}
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['projects'], 'readwrite');
      const store = transaction.objectStore('projects');
      
      const request = store.put({
        ...project,
        lastModified: Date.now()
      });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to save project'));
    });
  }

  async saveDraft(slides: Slide[]): Promise<void> {
    if (!this.db) {await this.initDB();}
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['drafts'], 'readwrite');
      const store = transaction.objectStore('drafts');
      
      const draft = {
        id: 'current-draft',
        slides,
        lastModified: Date.now()
      };

      const request = store.put(draft);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to save draft'));
    });
  }

  async loadDraft(): Promise<Slide[] | null> {
    if (!this.db) {await this.initDB();}
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['drafts'], 'readonly');
      const store = transaction.objectStore('drafts');
      
      const request = store.get('current-draft');

      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.slides : null);
      };
      
      request.onerror = () => reject(new Error('Failed to load draft'));
    });
  }

  async getProjects(): Promise<SlideProject[]> {
    if (!this.db) {await this.initDB();}
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['projects'], 'readonly');
      const store = transaction.objectStore('projects');
      
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(new Error('Failed to load projects'));
    });
  }

  async deleteProject(id: string): Promise<void> {
    if (!this.db) {await this.initDB();}
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['projects'], 'readwrite');
      const store = transaction.objectStore('projects');
      
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to delete project'));
    });
  }

  // User Preferences Methods
  async saveUserPreferences(preferences: Omit<UserPreferences, 'id' | 'lastModified'>): Promise<void> {
    if (!this.db) {await this.initDB();}
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['preferences'], 'readwrite');
      const store = transaction.objectStore('preferences');
      
      const prefData: UserPreferences = {
        id: 'user-preferences',
        ...preferences,
        lastModified: Date.now()
      };

      const request = store.put(prefData);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to save user preferences'));
    });
  }

  async loadUserPreferences(): Promise<UserPreferences | null> {
    if (!this.db) {await this.initDB();}
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['preferences'], 'readonly');
      const store = transaction.objectStore('preferences');
      
      const request = store.get('user-preferences');

      request.onsuccess = () => {
        resolve(request.result || null);
      };
      
      request.onerror = () => reject(new Error('Failed to load user preferences'));
    });
  }

  // App State Backup Methods
  async saveAppStateBackup(state: Partial<SlideGeneratorState>, slides: Slide[], activeSlideId: string | null): Promise<void> {
    if (!this.db) {await this.initDB();}
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['backups'], 'readwrite');
      const store = transaction.objectStore('backups');
      
      const backup: AppStateBackup = {
        id: `backup-${Date.now()}`,
        state,
        slides,
        activeSlideId,
        timestamp: Date.now()
      };

      const request = store.put(backup);

      request.onsuccess = () => {
        // Keep only the last 10 backups
        this.cleanupOldBackups();
        resolve();
      };
      request.onerror = () => reject(new Error('Failed to save app state backup'));
    });
  }

  async loadLatestAppStateBackup(): Promise<AppStateBackup | null> {
    if (!this.db) {await this.initDB();}
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['backups'], 'readonly');
      const store = transaction.objectStore('backups');
      const index = store.index('timestamp');
      
      // Get the latest backup
      const request = index.openCursor(null, 'prev');

      request.onsuccess = () => {
        const cursor = request.result;
        resolve(cursor ? cursor.value : null);
      };
      
      request.onerror = () => reject(new Error('Failed to load app state backup'));
    });
  }

  private async cleanupOldBackups(): Promise<void> {
    if (!this.db) {return;}
    
    try {
      const transaction = this.db.transaction(['backups'], 'readwrite');
      const store = transaction.objectStore('backups');
      const index = store.index('timestamp');
      
      // Get all backups sorted by timestamp (newest first)
      const request = index.openCursor(null, 'prev');
      let count = 0;
      
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          count++;
          // Delete backups beyond the 10 most recent
          if (count > 10) {
            cursor.delete();
          }
          cursor.continue();
        }
      };
    } catch (error) {
      console.warn('Failed to cleanup old backups:', error);
    }
  }

  // Migration method to move localStorage data to IndexedDB
  async migrateFromLocalStorage(): Promise<void> {
    try {
      // Migrate slides if they exist in localStorage
      const savedSlides = localStorage.getItem('slides');
      const savedActiveSlideId = localStorage.getItem('activeSlideId');
      
      if (savedSlides) {
        const parsedSlides = JSON.parse(savedSlides);
        if (Array.isArray(parsedSlides) && parsedSlides.length > 0) {
          await this.saveDraft(parsedSlides);
          
          // Create a backup of the current state
          await this.saveAppStateBackup(
            { activeSlideId: savedActiveSlideId },
            parsedSlides,
            savedActiveSlideId
          );
          
          // Clear from localStorage after successful migration
          localStorage.removeItem('slides');
          localStorage.removeItem('activeSlideId');
        }
      }

      // Migrate user preferences
      const collapsedSections = localStorage.getItem('collapsedSections');
      const propertyPanelPosition = localStorage.getItem('propertyPanelPosition');
      const hideKeyboardHints = localStorage.getItem('hideKeyboardHints');
      
      if (collapsedSections || propertyPanelPosition || hideKeyboardHints) {
        await this.saveUserPreferences({
          collapsedSections: collapsedSections ? JSON.parse(collapsedSections) : {},
          propertyPanelPosition: propertyPanelPosition ? JSON.parse(propertyPanelPosition) : { x: 400, y: 80 },
          hideKeyboardHints: hideKeyboardHints === 'true'
        });
        
        // Clear from localStorage after successful migration
        localStorage.removeItem('collapsedSections');
        localStorage.removeItem('propertyPanelPosition');
        localStorage.removeItem('hideKeyboardHints');
      }
      
    } catch (error) {
      console.warn('Failed to migrate data from localStorage:', error);
    }
  }
}

export const indexedDBService = new IndexedDBService();
export type { SlideProject, UserPreferences, AppStateBackup };